import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  factTable,
  fmtDateTime,
  h1,
  signatureBlock,
  table,
} from "@/lib/documentGenerator/docTheme";
import {
  INCIDENT_TYPE_LABELS,
  INCIDENT_STATUS_LABELS,
} from "@/lib/basProcess/constants";
import type { IncidentReport, IncidentPerson } from "@/types/incidentReport";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

const formatPersonList = (persons: IncidentPerson[]) =>
  persons.length
    ? persons.map((p) => [p.name, p.company, p.role].filter(Boolean).join(", ")).join("\n")
    : "";

export const generateIncidentReportWordDocument = async (
  report: IncidentReport,
  projectName?: string
) => {
  const documentName = "Tillbud- och olycksrapportering";
  const stamp = await createStampForCurrentUser({
    docNumber: "28",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Grunduppgifter"),
    factTable([
      ["Rapportnummer", report.id.slice(0, 8).toUpperCase()],
      ["Typ", INCIDENT_TYPE_LABELS[report.incident_type] || report.incident_type],
      ["Status", INCIDENT_STATUS_LABELS[report.status] || report.status],
      ["Tidpunkt", fmtDateTime(report.occurred_at)],
      ["Plats", report.location],
    ]),

    h1("2. Händelsebeskrivning"),
    body(report.description || ""),

    h1("3. Inblandade personer"),
    table(
      ["Namn", "Företag", "Roll", "Skadad"],
      (report.involved_persons || []).map((p) => [
        p.name,
        p.company,
        p.role,
        p.injured ? "Ja" : "Nej",
      ]),
      { columnWidths: [2400, 2400, 2400, 2160] }
    ),

    h1("4. Vittnen"),
    body(formatPersonList(report.witnesses || [])),

    h1("5. Omedelbara åtgärder"),
    body(report.immediate_actions || ""),

    h1("6. Utredning"),
    factTable([
      ["Rotorsak", report.root_cause],
      ["Konsekvenser", report.consequences],
      ["Åtgärdsansvarig", report.assigned_to],
    ]),

    h1("7. Anmälan till Arbetsmiljöverket"),
    factTable([
      ["Anmäld till AMV", report.reported_to_amv ? "Ja" : "Nej"],
      ["Anmälningsdatum", report.amv_report_date],
      ["Diarienummer", report.amv_case_number],
    ]),
  ];

  if (report.status === "closed") {
    children.push(
      h1("8. Avslutning"),
      factTable([
        ["Avslutad", report.closed_at ? fmtDateTime(report.closed_at) : ""],
        ["Avslutningsnotering", report.closure_note],
      ])
    );
  }
  const footer = await documentFooter({ docNumber: "28", documentType: "tillbud" });


  const doc = buildDocument({
    meta: {
      version: footer.version,
      review: footer.review,
      docNumber: "28",
      documentName,
      projectName,
      status: INCIDENT_STATUS_LABELS[report.status] || report.status,
      date: report.occurred_at,
      extra: [["Rapportnummer", report.id.slice(0, 8).toUpperCase()]],
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `28_Tillbud_olycka_${report.id.slice(0, 8)}.docx`, {
    documentType: "tillbud",
    docNumber: "28",
    documentId: report.id,
    documentName: `Tillbud/olycka ${report.id.slice(0, 8).toUpperCase()}`,
    snapshot: report,
    stamp,
  });
};
